import { DOCUMENT_LABELS } from '../config/documentLabels';
import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { IngestionItem } from '../store/useFormCGIAStore';

import { pickSlotRepresentatives } from './docRowSelection';

export interface FailedUpload {
  id: string;
  fileName: string;
  /** Raison lisible par l'agence (pas le message technique brut). */
  reason: string;
}

const REASON_BY_STATUS: Record<string, string> = {
  error: "Le fichier n'a pas pu être analysé",
  mismatch: 'Type de document non reconnu',
  unreadable: 'Document illisible',
};

/**
 * Raison affichée pour une pièce en échec. Une raison DESTINÉE À L'AGENCE
 * (`userFacingReason` : fichier trop lourd, format non accepté) prime toujours
 * sur le libellé générique du statut : sans elle, un scan de 21 Mo refusé par
 * Firebase s'affichait « Le fichier n'a pas pu être analysé » et l'agence
 * redéposait le même fichier indéfiniment (dossier FW-262-LA, 2026-08).
 */
const reasonFor = (it: IngestionItem): string =>
  it.userFacingReason ?? REASON_BY_STATUS[it.status] ?? "Le fichier n'a pas pu être analysé";

/**
 * Pièces déposées dont l'analyse a échoué SANS type de document identifiable
 * (`docTypeDetected` absent ou `'unknown'`).
 *
 * Conservé pour compatibilité / tests : c'est le SOUS-ENSEMBLE « échecs sans
 * type » de `selectReceivedUploads`. En prod on affiche désormais l'ensemble
 * plus large via `selectReceivedUploads` (voir ci-dessous).
 */
export const selectFailedUploads = (ingestionItems: Record<string, IngestionItem>): FailedUpload[] =>
  Object.values(ingestionItems)
    .filter(
      (it) =>
        (it.status === 'error' || it.status === 'mismatch' || it.status === 'unreadable') &&
        (!it.docTypeDetected || it.docTypeDetected === 'unknown'),
    )
    .map((it) => ({
      id: it.id,
      fileName: it.fileName,
      reason: reasonFor(it),
    }));

/**
 * Catégorie d'une pièce reçue mais NON rattachée à un slot de la checklist :
 *  - `failed` : le fichier a échoué (illisible, type non reconnu) → à redéposer.
 *  - `extra`  : le fichier a bien été analysé mais n'est pas le représentant de
 *    son slot (verso, 2ᵉ face, doublon d'un scan déjà pris en compte). Rien à
 *    faire — on l'affiche uniquement pour rassurer : « c'est bien arrivé ».
 */
export type ReceivedUploadCategory = 'failed' | 'extra';

export interface ReceivedUpload {
  id: string;
  fileName: string;
  category: ReceivedUploadCategory;
  /** Libellé lisible affiché à droite du nom de fichier. */
  reason: string;
}

const FAILED_STATUSES: ReadonlySet<IngestionItem['status']> = new Set<IngestionItem['status']>([
  'error',
  'mismatch',
  'unreadable',
]);
const IN_PROGRESS_STATUSES: ReadonlySet<IngestionItem['status']> = new Set<IngestionItem['status']>([
  'queued',
  'uploading',
  'analyzing',
]);

/**
 * TOUTES les pièces déposées qui n'apparaissent PAS comme représentant d'un slot
 * de la checklist Documents — pour qu'aucun fichier envoyé ne disparaisse
 * silencieusement de l'écran (retour Biwiz 2026-07 : « il faut impérativement
 * que toutes les pièces soient prises même si elles ne sont pas reconnues »).
 *
 * Deux cas remontés :
 *  1. Échecs (`failed`) — type non reconnu ou fichier illisible, quel que soit
 *     le type : l'agence doit les redéposer. (Superset de `selectFailedUploads`,
 *     qui ne couvrait que les échecs SANS type.)
 *  2. Pièces reçues en plus (`extra`) — analysées avec succès mais non retenues
 *     comme représentant de leur slot (le meilleur scan gagne, cf.
 *     `pickSlotRepresentatives`) : verso, 2ᵉ face, doublon. On les affiche en
 *     « bien reçu » pour que l'agence ne croie pas qu'elles ont été ignorées.
 *
 * Les items encore en file / upload / analyse sont ignorés (bruit transitoire :
 * ils apparaissent déjà « en cours » dans la checklist ou y basculeront).
 */
export const selectReceivedUploads = (
  ingestionItems: Record<string, IngestionItem>,
  titulaireCniUrl?: string,
): ReceivedUpload[] => {
  const representativeIds = new Set(
    [...pickSlotRepresentatives(ingestionItems, titulaireCniUrl).values()].map((it) => it.id),
  );

  return Object.values(ingestionItems)
    .filter((it) => !representativeIds.has(it.id))
    .filter((it) => !IN_PROGRESS_STATUSES.has(it.status))
    .map((it) => {
      if (FAILED_STATUSES.has(it.status)) {
        return {
          id: it.id,
          fileName: it.fileName,
          category: 'failed' as const,
          reason: reasonFor(it),
        };
      }
      // Statut terminal non-échec (`ok`) mais pas représentant → pièce reçue en
      // plus. On indique le type reconnu quand on l'a, pour situer la pièce.
      const label =
        it.docTypeDetected && it.docTypeDetected !== 'unknown'
          ? DOCUMENT_LABELS[it.docTypeDetected as CgDocType]?.title
          : undefined;
      return {
        id: it.id,
        fileName: it.fileName,
        category: 'extra' as const,
        reason: label ? `Bien reçu — ${label}` : 'Bien reçu',
      };
    });
};
